import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Menu, Search, Sun, Moon, Sprout, ChevronDown,
  Home, Compass, Clock, Sparkles, BookOpen,
  Activity, Heart, Smile, Utensils, Leaf
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import SearchModal from "@/components/SearchModal";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const slug = (s) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, "-");
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const PRIMARY_NAV = [
  { to: "/", label: "Início", end: true },
  { to: "/sintomas", label: "Sintomas" },
  { to: "/orgaos", label: "Órgãos" },
  { to: "/relogio", label: "Relógio 24h" },
  { to: "/elementos", label: "Elementos" },
  { to: "/emocoes", label: "Emoções" },
  { to: "/autocuidado", label: "Autocuidado" },
  { to: "/registros", label: "Registros" },
];

export const GUIDE_NAV = [
  { to: "/sabores", label: "Sabores" },
  { to: "/alimentos", label: "Alimentos" },
  { to: "/chas", label: "Chás" },
  { to: "/seguranca", label: "Segurança" },
];

export const EXPLORE_ITEMS = [
  { to: "/sintomas", label: "Sintomas", desc: "O que você sente e possíveis relações", icon: Activity },
  { to: "/orgaos", label: "12 Órgãos", desc: "Funções, hábitos e horários de cada órgão", icon: Heart },
  { to: "/elementos", label: "Cinco Elementos", desc: "Madeira, Fogo, Terra, Metal e Água", icon: Compass },
  { to: "/emocoes", label: "Emoções", desc: "Como os sentimentos se conectam ao corpo", icon: Smile },
  { to: "/sabores", label: "Sabores", desc: "Qualidades energéticas na MTC", icon: Sparkles },
  { to: "/alimentos", label: "Alimentos", desc: "Alimentos que apoiam cada elemento", icon: Utensils },
  { to: "/chas", label: "Chás", desc: "Chás tradicionais e recomendações", icon: Leaf },
];

const ALL_NAV = [...PRIMARY_NAV, ...GUIDE_NAV];

function Brand({ onClick }) {
  return (
    <Link to="/" onClick={onClick} data-testid="brand-logo" className="flex items-center gap-2.5 group">
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform group-hover:scale-105">
        <Sprout className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <span className="leading-tight">
        <span className="block font-serif text-lg font-semibold tracking-tight text-foreground">Corpo &amp; Conexões</span>
        <span className="block text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Guia Visual de MTC</span>
      </span>
    </Link>
  );
}

export default function Layout({ children }) {
  const { theme, toggle } = useTheme();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const location = useLocation();

  const explorePrefixes = [
    "/sintomas",
    "/orgaos",
    "/elementos",
    "/emocoes",
    "/sabores",
    "/alimentos",
    "/chas",
  ];
  const isExploreActive = explorePrefixes.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );

  const linkClass = ({ isActive }) =>
    `relative px-3 py-2 text-sm font-medium rounded-full transition-colors ${
      isActive ? "text-primary-foreground bg-primary" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
    }`;

  return (
    <div className="min-h-screen flex flex-col grain">
      <header className="sticky top-0 z-50 border-b border-gold/20 bg-background/85 backdrop-blur-md">
        <div className="container-app flex h-16 items-center justify-between gap-4">
          <Brand />

          <nav className="hidden xl:flex items-center gap-1">
            {PRIMARY_NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={linkClass} data-testid={`nav-${slug(n.label)}`}>
                {n.label}
              </NavLink>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-mais"
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary"
                >
                  Guia <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {GUIDE_NAV.map((n) => (
                  <DropdownMenuItem key={n.to} asChild>
                    <Link to={n.to} data-testid={`nav-drop-${slug(n.label)}`}>{n.label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          <div className="flex items-center gap-1.5">
            <button
              data-testid="open-search"
              onClick={() => setSearchOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
              aria-label="Buscar"
            >
              <Search className="h-5 w-5" strokeWidth={1.75} />
            </button>
            <button
              data-testid="toggle-theme"
              onClick={toggle}
              className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
              aria-label="Alternar tema"
            >
              {theme === "dark" ? <Sun className="h-5 w-5" strokeWidth={1.75} /> : <Moon className="h-5 w-5" strokeWidth={1.75} />}
            </button>

            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="open-menu"
                  className="xl:hidden flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary hover:text-foreground"
                  aria-label="Menu"
                >
                  <Menu className="h-5 w-5" strokeWidth={1.75} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 bg-background">
                <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
                <div className="mb-6">
                  <Brand onClick={() => setMenuOpen(false)} />
                </div>
                <nav className="flex flex-col gap-1">
                  {ALL_NAV.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      onClick={() => setMenuOpen(false)}
                      data-testid={`mnav-${slug(n.label)}`}
                      className={({ isActive }) =>
                        `rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                          isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"
                        }`
                      }
                    >
                      {n.label}
                    </NavLink>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div key={location.pathname} className="container-app py-8 sm:py-12 lg:py-16 animate-fade-up">{children}</div>
      </main>

      <footer className="border-t border-gold/20 bg-card/50 pb-20 xl:pb-0">
        <div className="container-app py-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <Brand />
            <p className="text-xs text-muted-foreground max-w-md leading-relaxed">
              Material educativo inspirado na Medicina Tradicional Chinesa. Não é diagnóstico, tratamento ou prescrição e
              não substitui a avaliação de profissionais de saúde.
            </p>
          </div>
          <p className="mt-6 text-[11px] text-muted-foreground">Corpo &amp; Conexões — Guia Visual Completo.</p>
        </div>
      </footer>

      {/* GAVETA INFERIOR (BOTTOM SHEET) DE EXPLORAÇÃO */}
      <Sheet open={exploreOpen} onOpenChange={setExploreOpen}>
        <SheetContent
          side="bottom"
          data-testid="bottom-sheet-explorar"
          className="rounded-t-3xl border-t border-gold/30 bg-background p-6 max-h-[85vh] overflow-y-auto xl:hidden"
        >
          <div className="border-b border-border/60 pb-3 mb-4">
            <SheetTitle className="text-lg font-serif font-semibold text-foreground flex items-center gap-2">
              <Compass className="h-5 w-5 text-gold" /> Explorar o Guia
            </SheetTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              Consulte órgãos, sintomas, elementos e práticas da MTC
            </p>
          </div>
          <div className="grid gap-2">
            {EXPLORE_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setExploreOpen(false)}
                  data-testid={`explore-item-${item.to.replace("/", "")}`}
                  className={`flex items-center gap-3.5 rounded-2xl p-3 transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "bg-secondary/40 hover:bg-secondary text-foreground"
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                      isActive ? "bg-primary-foreground/20 text-primary-foreground" : "bg-gold/15 text-gold"
                    }`}
                  >
                    <Icon className="h-5 w-5" strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium leading-snug">{item.label}</p>
                    <p className={`text-xs truncate ${isActive ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{item.desc}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      {/* BOTTOM NAVIGATION (EXCLUSIVA PARA MOBILE / < 1280px) */}
      <nav
        data-testid="bottom-nav"
        aria-label="Navegação rápida inferior"
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-gold/20 bg-background/95 backdrop-blur-lg pb-[calc(0.25rem+env(safe-area-inset-bottom,0px))] pt-1.5 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] xl:hidden"
      >
        <div className="flex items-center justify-around px-1 max-w-lg mx-auto">
          {/* 1. Início */}
          <Link
            to="/"
            data-testid="bottom-nav-inicio"
            aria-current={location.pathname === "/" ? "page" : undefined}
            className="group flex flex-1 flex-col items-center justify-center py-1 px-0.5 min-w-0"
          >
            <span
              className={`flex h-7 w-11 items-center justify-center rounded-full transition-all ${
                location.pathname === "/"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground group-hover:text-foreground"
              }`}
            >
              <Home className="h-4 w-4" strokeWidth={location.pathname === "/" ? 2.25 : 1.75} />
            </span>
            <span
              className={`text-[10px] tracking-tight mt-1 transition-colors truncate max-w-full ${
                location.pathname === "/" ? "font-semibold text-primary" : "font-normal text-muted-foreground"
              }`}
            >
              Início
            </span>
          </Link>

          {/* 2. Explorar */}
          <button
            type="button"
            data-testid="bottom-nav-explorar"
            onClick={() => setExploreOpen(true)}
            aria-label="Explorar conteúdos"
            className="group flex flex-1 flex-col items-center justify-center py-1 px-0.5 min-w-0"
          >
            <span
              className={`flex h-7 w-11 items-center justify-center rounded-full transition-all ${
                isExploreActive
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground group-hover:text-foreground"
              }`}
            >
              <Compass className="h-4 w-4" strokeWidth={isExploreActive ? 2.25 : 1.75} />
            </span>
            <span
              className={`text-[10px] tracking-tight mt-1 transition-colors truncate max-w-full ${
                isExploreActive ? "font-semibold text-primary" : "font-normal text-muted-foreground"
              }`}
            >
              Explorar
            </span>
          </button>

          {/* 3. Relógio */}
          <Link
            to="/relogio"
            data-testid="bottom-nav-relogio"
            aria-current={location.pathname.startsWith("/relogio") ? "page" : undefined}
            className="group flex flex-1 flex-col items-center justify-center py-1 px-0.5 min-w-0"
          >
            <span
              className={`flex h-7 w-11 items-center justify-center rounded-full transition-all ${
                location.pathname.startsWith("/relogio")
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground group-hover:text-foreground"
              }`}
            >
              <Clock className="h-4 w-4" strokeWidth={location.pathname.startsWith("/relogio") ? 2.25 : 1.75} />
            </span>
            <span
              className={`text-[10px] tracking-tight mt-1 transition-colors truncate max-w-full ${
                location.pathname.startsWith("/relogio") ? "font-semibold text-primary" : "font-normal text-muted-foreground"
              }`}
            >
              Relógio
            </span>
          </Link>

          {/* 4. Autocuidado */}
          <Link
            to="/autocuidado"
            data-testid="bottom-nav-autocuidado"
            aria-current={location.pathname.startsWith("/autocuidado") ? "page" : undefined}
            className="group flex flex-1 flex-col items-center justify-center py-1 px-0.5 min-w-0"
          >
            <span
              className={`flex h-7 w-11 items-center justify-center rounded-full transition-all ${
                location.pathname.startsWith("/autocuidado")
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground group-hover:text-foreground"
              }`}
            >
              <Sparkles className="h-4 w-4" strokeWidth={location.pathname.startsWith("/autocuidado") ? 2.25 : 1.75} />
            </span>
            <span
              className={`text-[10px] tracking-tight mt-1 transition-colors truncate max-w-full ${
                location.pathname.startsWith("/autocuidado") ? "font-semibold text-primary" : "font-normal text-muted-foreground"
              }`}
            >
              Autocuidado
            </span>
          </Link>

          {/* 5. Registros */}
          <Link
            to="/registros"
            data-testid="bottom-nav-registros"
            aria-current={location.pathname.startsWith("/registros") ? "page" : undefined}
            className="group flex flex-1 flex-col items-center justify-center py-1 px-0.5 min-w-0"
          >
            <span
              className={`flex h-7 w-11 items-center justify-center rounded-full transition-all ${
                location.pathname.startsWith("/registros")
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground group-hover:text-foreground"
              }`}
            >
              <BookOpen className="h-4 w-4" strokeWidth={location.pathname.startsWith("/registros") ? 2.25 : 1.75} />
            </span>
            <span
              className={`text-[10px] tracking-tight mt-1 transition-colors truncate max-w-full ${
                location.pathname.startsWith("/registros") ? "font-semibold text-primary" : "font-normal text-muted-foreground"
              }`}
            >
              Registros
            </span>
          </Link>
        </div>
      </nav>

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
      <ScrollToTopButton />
    </div>
  );
}
